import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Star, Clock, ArrowLeft, Plus, Utensils } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useCart } from "@/lib/cart-context";
import { useCountry } from "@/lib/country-context";
import { useToast } from "@/hooks/use-toast";
import api from "@/lib/api";

export default function RestaurantDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { formatPrice } = useCountry();
  const { toast } = useToast();

  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState(null);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/restaurants/${id}`)
      .then(({ data }) => {
        setRestaurant(data);
        if (data.categories?.length) setActiveCat(data.categories[0].id);
      })
      .catch(() => setRestaurant(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="h-56 bg-muted animate-pulse" />
        <div className="p-6 space-y-4">
          <Skeleton className="h-8 w-1/2" />
          <Skeleton className="h-4 w-1/4" />
          <div className="grid gap-3 mt-8">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-20 w-full rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground">Restaurant not found</p>
          <Button variant="link" onClick={() => navigate("/restaurants")}>Go back</Button>
        </div>
      </div>
    );
  }

  const items = (restaurant.menu_items || []).filter(
    (i) => i.category_id === activeCat && i.is_available
  );

  const addCart = (it) => {
    addItem(
      { menuItemId: it.id, name: it.name, price: it.price, quantity: 1 },
      restaurant.id,
      restaurant.name
    );
    toast({ title: "Added to cart", description: `${it.name} added` });
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="relative h-56 bg-muted overflow-hidden">
        {restaurant.image_url ? (
          <img src={restaurant.image_url} alt={restaurant.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Utensils className="h-16 w-16 text-muted-foreground/20" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
        <button
          onClick={() => navigate("/restaurants")}
          className="absolute top-4 left-4 bg-background/80 backdrop-blur rounded-full p-2 hover:bg-background transition-colors"
          data-testid="restaurant-back-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
      </div>

      <div className="px-4 md:px-6 py-4 border-b bg-card">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="font-serif text-2xl font-bold" data-testid="restaurant-name">{restaurant.name}</h1>
              <p className="text-muted-foreground mt-0.5">{restaurant.cuisine}</p>
              {restaurant.address && (
                <p className="text-sm text-muted-foreground mt-0.5">{restaurant.address}</p>
              )}
            </div>
            {!restaurant.is_open && <Badge variant="secondary">Closed</Badge>}
          </div>
          <div className="flex items-center gap-5 mt-3 text-sm">
            <span className="flex items-center gap-1.5 text-amber-500 font-semibold">
              <Star className="h-4 w-4 fill-amber-500" />
              {restaurant.rating.toFixed(1)}
            </span>
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <Clock className="h-4 w-4" />
              {restaurant.delivery_time} min
            </span>
            <span className="text-muted-foreground">{formatPrice(restaurant.delivery_fee)} delivery</span>
          </div>
        </div>
      </div>

      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b px-4 md:px-6">
        <div className="max-w-3xl mx-auto overflow-x-auto no-scrollbar">
          <div className="flex gap-1 py-3">
            {(restaurant.categories || []).map((c) => (
              <button
                key={c.id}
                onClick={() => setActiveCat(c.id)}
                data-testid={`restaurant-tab-${c.id}`}
                className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                  activeCat === c.id
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="px-4 md:px-6 py-6">
        <div className="max-w-3xl mx-auto space-y-3">
          {items.length === 0 && (
            <p className="text-center text-muted-foreground py-12">No items in this category yet.</p>
          )}
          {items.map((it) => (
            <div
              key={it.id}
              className="flex items-start gap-4 bg-card border rounded-2xl p-4 hover:shadow-sm transition-shadow"
              data-testid={`menu-item-${it.id}`}
            >
              {it.image_url ? (
                <div className="w-24 h-24 rounded-xl overflow-hidden shrink-0 bg-muted">
                  <img src={it.image_url} alt={it.name} className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="w-24 h-24 rounded-xl bg-muted flex items-center justify-center shrink-0">
                  <Utensils className="h-8 w-8 text-muted-foreground/20" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-semibold leading-tight">{it.name}</h3>
                    {it.is_popular && (
                      <Badge className="text-xs mt-1 bg-amber-100 text-amber-700 border-amber-200">Popular</Badge>
                    )}
                  </div>
                </div>
                {it.description && (
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{it.description}</p>
                )}
                <div className="flex items-center justify-between mt-3">
                  <span className="font-bold text-primary text-lg">{formatPrice(it.price)}</span>
                  <Button
                    size="sm" className="h-8 rounded-xl gap-1.5"
                    onClick={() => addCart(it)}
                    disabled={!restaurant.is_open}
                    data-testid={`menu-add-${it.id}`}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
